import uniq from "lodash/uniq";
import type { Category, CategoryContentField } from "./types";

/**
 * Strips out sub-subcategories from a nested category tree.
 */
export const removeSubSubcategories = (categories: Category[]): Category[] =>
  categories.map(category => ({
    ...category,
    subcategories: category.subcategories?.map(category => ({
      ...category,
      subcategories: undefined,
    })),
  }));

export const flattenCategories = (categories: Category[] = []): Category[] =>
  categories.flatMap(({ subcategories: nested = [], ...rest }) => [
    rest,
    ...flattenCategories(nested),
  ]);

export const getLeafCategoryIds = (categories: Category[]): string[] =>
  categories.flatMap(category =>
    category.subcategories?.length
      ? getLeafCategoryIds(category.subcategories)
      : [category.id],
  );

export const extractContentIds = (
  field: CategoryContentField,
  categories: Category[],
): string[] => {
  const leafIds = getLeafCategoryIds(categories);
  const result: string[] = [];

  const traverse = (category: Category) => {
    if (leafIds.includes(category.id)) {
      const contentIds = category[field] ?? [];
      result.push(...contentIds);
      return;
    }

    category.subcategories?.forEach(traverse);
  };

  categories.forEach(traverse);

  return uniq(result);
};

export const filterCategoriesByCategoryIds = (
  categories: Category[],
  categoryIds: string[],
): Category[] => {
  const filterRecursive = (category: Category): Category | null => {
    const hasDirectMatch = categoryIds.includes(category.id);

    const filteredSubcategories =
      category.subcategories
        ?.map(filterRecursive)
        .filter((c): c is Category => c !== null) ?? [];

    if (hasDirectMatch || filteredSubcategories.length > 0) {
      return {
        ...category,
        subcategories: filteredSubcategories,
      };
    }

    return null;
  };

  return categories
    .map(filterRecursive)
    .filter((c): c is Category => c !== null);
};

/*
 * Recursively filters a nested category tree so that only categories
 * containing question ids from the provided list are retained.
 *
 * A category is preserved if it directly contains at least one matching
 * question id or any of its descendant categories contain matching
 * question ids.
 */
export const filterCategoriesByContentIds = (
  field: CategoryContentField,
  categories: Category[],
  contentIds: string[],
): Category[] => {
  const contentSet = new Set(contentIds);

  const filterRecursive = (category: Category): Category | null => {
    const filteredChildren = (category.subcategories ?? [])
      .map(filterRecursive)
      .filter((c): c is Category => c !== null);

    const hasOwnContent = (category[field] ?? []).some(id =>
      contentSet.has(id),
    );

    if (!hasOwnContent && !filteredChildren.length) {
      return null;
    }

    return {
      ...category,
      subcategories: filteredChildren,
    };
  };

  return categories
    .map(filterRecursive)
    .filter((c): c is Category => c !== null);
};

export const getDisabledCategoryIds = (
  field: CategoryContentField,
  categories: Category[],
  disabledContentIds: string[],
): string[] => {
  const result: string[] = [];

  const traverse = (category: Category) => {
    const contentIds = category[field] ?? [];

    const isDisabled =
      contentIds.length > 0 &&
      contentIds.every(id => disabledContentIds.includes(id));

    if (isDisabled) {
      result.push(category.id);
    }

    category.subcategories?.forEach(traverse);
  };

  categories.forEach(traverse);

  return result;
};
